import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { 
  CheckCircle, 
  ArrowRight, 
  Shield, 
  Zap, 
  FileCheck, 
  Building, 
  Calculator,
  Download,
  Bell,
  Lock,
  Eye,
  BarChart3,
  Clock,
  Star,
  Menu,
  Sparkles,
  TrendingUp,
  Target,
  Brain,
  Cpu,
  Database,
  Linkedin,
  ChevronDown,
  Users
} from "lucide-react";
import { mockData } from "../data/mock";

import BlogSection from "./BlogSection";
import PricingSection from "./PricingSection";
import FAQSection from "./FAQSection";
import FormlessWidget from "./FormlessWidget";

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState("connect");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleWaitlistJoin = (e) => {
    e.preventDefault();
    // Redirect to external Typeform for waitlist signup
    window.open('https://form.typeform.com/to/mstSUQPT', '_blank');
  };

  const handleEligibilityCheck = (e) => {
    e.preventDefault();
    // Trigger the Formless widget for eligibility check
    try {
      // Look for the Formless widget trigger and click it
      const formlessButton = document.querySelector('[data-formless-widget], .formless-widget-trigger, [class*="formless"]');
      if (formlessButton) {
        formlessButton.click();
      } else {
        // Fallback: If Formless widget isn't ready, open a new window to the conversation
        window.open('https://app.formless.ai/conversations/b54qzzsJ66SR', '_blank');
      }
    } catch (error) {
      console.error('Error triggering Formless widget:', error);
      // Final fallback
      window.open('https://app.formless.ai/conversations/b54qzzsJ66SR', '_blank');
    }
  };

  const handleScheduleCall = (e) => {
    e.preventDefault();
    // Redirect to Calendly for scheduling calls
    window.open('https://calendly.com/advisorxai/30min', '_blank');
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" itemScope itemType="https://schema.org/WebPage">
      {/* Semantic Header */}
      <header role="banner">
        {/* Navigation - Enhanced spacing and typography with gradient - no border */}
      <nav className="fixed top-0 left-0 right-0 z-50 text-white backdrop-blur-md border-b border-white/10" style={{background: '#112b3c'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img 
                src="/credentra-logo-new.png" 
                alt="Credentra Logo" 
                className="h-52 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <div className="nav-links">
                <a href="#features" className="nav-link">Features</a>
                <a href="#case-study" className="nav-link">Case Study</a>
                <a href="/about" className="nav-link">About</a>
                <a href="/cpas" className="nav-link">For CPAs</a>
                
                {/* Resources Dropdown */}
                <div className="relative">
                  <button 
                    className="nav-link flex items-center gap-1"
                    onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
                    onMouseEnter={() => setResourcesDropdownOpen(true)}
                    onMouseLeave={() => setResourcesDropdownOpen(false)}
                  >
                    Resources
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${resourcesDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {resourcesDropdownOpen && (
                    <div 
                      className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                      onMouseEnter={() => setResourcesDropdownOpen(true)}
                      onMouseLeave={() => setResourcesDropdownOpen(false)}
                    >
                      <div className="py-2">
                        <a 
                          href="/blog" 
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-credentra-bright-teal transition-colors"
                        >
                          <Calculator className="h-4 w-4 mr-3 text-blue-600" />
                          <div>
                            <div className="font-medium">Tax Recovery Insights</div>
                            <div className="text-sm text-gray-500">FICA tip credits & compliance</div>
                          </div>
                        </a>
                        <a 
                          href="/blog" 
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-credentra-bright-teal transition-colors"
                        >
                          <Brain className="h-4 w-4 mr-3 text-purple-600" />
                          <div>
                            <div className="font-medium">AI in Finance & Accounting</div>
                            <div className="text-sm text-gray-500">AI research & automation</div>
                          </div>
                        </a>
                        <a 
                          href="/blog" 
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-credentra-bright-teal transition-colors"
                        >
                          <Users className="h-4 w-4 mr-3 text-green-600" />
                          <div>
                            <div className="font-medium">CPA Resources</div>
                            <div className="text-sm text-gray-500">Professional resources for firms</div>
                          </div>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                
                <a href="#pricing" className="nav-link">Pricing</a>
              </div>
              
              {/* Social Media Icons */}
              <div className="flex items-center space-x-2">
                <a 
                  href="https://www.linkedin.com/company/credentra" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-credentra-bright-teal transition-colors duration-300 p-2 hover:bg-white/10 rounded-lg"
                  aria-label="Follow us on LinkedIn"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.youtube.com/@Credentra" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-credentra-bright-teal transition-colors duration-300 p-2 hover:bg-white/10 rounded-lg"
                  aria-label="Subscribe to our YouTube channel"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                className="md:hidden text-white hover:text-credentra-bright-teal transition-colors w-12 h-12 flex items-center justify-center"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-black/20 backdrop-blur-sm">
              <div className="px-6 py-6 space-y-4">
                <a href="#features" className="block nav-link">Features</a>
                <a href="#case-study" className="block nav-link">Case Study</a>
                <a href="/about" className="block nav-link">About</a>
                <a href="/cpas" className="block nav-link">For CPAs</a>
                <a href="/blog" className="block nav-link">Resources</a>
                <a href="#pricing" className="block nav-link">Pricing</a>
                
                {/* Mobile Social Media Icons */}
                <div className="flex items-center justify-center space-x-6 mt-6 pt-6 border-t border-white/20">
                  <a 
                    href="https://www.linkedin.com/company/credentra" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:text-credentra-bright-teal transition-colors duration-300 p-3 hover:bg-white/10 rounded-lg"
                    aria-label="Follow us on LinkedIn"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.youtube.com/@Credentra" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:text-credentra-bright-teal transition-colors duration-300 p-3 hover:bg-white/10 rounded-lg"
                    aria-label="Subscribe to our YouTube channel"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                </div>
                
                <Button 
                  className="text-white font-semibold w-full mt-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                  style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
                  onClick={handleEligibilityCheck}
                >
                  Start Calculation
                </Button>
              </div>
            </div>
          )}
      </nav>
      </header>

      {/* Hero Section - Text Only, No Images */}
      <main role="main">
        <section className="text-white py-20 relative overflow-hidden" style={{background: '#112b3c'}} itemScope itemType="https://schema.org/Service">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            
            {/* Hero Badge */}
            <div className="text-center pt-1 pb-6 sm:pb-8">
              <div className="hero-badge inline-flex items-center gap-2 mb-8 sm:mb-12" style={{background: '#112b3c'}}>
                <Cpu className="h-4 w-4" />
                <span className="leading-tight" itemProp="category">
                  AI Tax-Credit Compliance for Hospitality Businesses
                </span>
              </div>
            </div>
          
          {/* Hero Content - Center Aligned, Text Only */}
          <div className="text-center hero-content">
            <h1 className={`hero-title ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.1s'}} itemProp="headline" itemScope itemType="https://schema.org/Service">
              <span itemProp="name">We Find Real Tax Credits from
              <span className="block">your Payroll & POS Data</span></span>
            </h1>
            
            <p className={`hero-subtitle ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.2s'}} itemProp="description" itemScope itemType="https://schema.org/Service">
              If your employees get tips, you can get tax credits worth<br />
              $10,000-$100,000 annually on your FICA taxes.
            </p>
            
            <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center mt-10 ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.3s'}}>
              <Button 
                size="lg"
                className="text-white font-semibold text-lg px-10 py-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
                onClick={handleEligibilityCheck}
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Start Calculation
              </Button>
            </div>
            
            {/* Trust Indicators - New Brand Colors */}
            <div className={`flex flex-wrap justify-center items-center gap-6 mt-16 ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.4s'}}>
              <div className="px-6 py-3 flex items-center gap-2 rounded-lg backdrop-blur-sm" style={{background: 'rgba(0, 214, 207, 0.1)', border: '1px solid rgba(0, 214, 207, 0.2)'}}>
                <Shield className="h-5 w-5 text-white" />
                <span className="text-sm text-white/90 font-medium">Privacy-first</span>
              </div>
              <div className="px-6 py-3 flex items-center gap-2 rounded-lg backdrop-blur-sm" style={{background: 'rgba(233, 30, 99, 0.1)', border: '1px solid rgba(233, 30, 99, 0.2)'}}>
                <FileCheck className="h-5 w-5 text-white" />
                <span className="text-sm text-white/90 font-medium">Audit trail exports</span>
              </div>
              <div className="glass-card px-6 py-3 flex items-center gap-2">
                <Building className="h-5 w-5 text-credentra-bright-teal" />
                <span className="text-sm text-white/90 font-medium">Built for F&B & CPAs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration only */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        </section>
      </main>

      {/* Case Study Section - Mark's Story */}
      <section id="case-study" className="py-24 bg-white relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="section-badge text-blue-600 bg-blue-50">
              <Eye className="h-3 w-3" />
              Case Study
            </div>
            <h2 className="enterprise-heading text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
              Watch Mark's Story
            </h2>
            <p className="enterprise-subheading text-xl max-w-3xl mx-auto text-gray-600">
              See how Fine Creek Brewing Company discovered thousands in FICA Tip Credits after 20+ years in business.
            </p>
          </div>
          
          {/* Video Container */}
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-100">
              <div className="relative pb-[56.25%] h-0"> {/* 16:9 aspect ratio */}
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/7Tw-UsQVvzY"
                  title="Mark's Story - Fine Creek Brewing Company"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Animated Data Flow Section - Completely Redesigned */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-full text-sm font-medium mb-6" style={{background: '#112b3c'}}>
              <Eye className="h-4 w-4" />
              Data Flow Process
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
              From Raw Data to Ready Forms
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Credentra AI securely transforms your payroll and POS data into
              <br />
              IRS-ready FICA tip credit forms
            </p>
          </div>
          
          {/* Professional Flow Container */}
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              
              {/* Flow Steps Container - Perfectly Aligned */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
                
                {/* Step 1: Data Sources */}
                <div className="text-center group">
                  <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-6 transform group-hover:scale-105 transition-all duration-300 h-[340px] flex flex-col justify-center relative overflow-hidden">
                    
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-blue-50 opacity-50"></div>
                    
                    <div className="relative z-10 space-y-6">
                      {/* Platform Logos */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-green-100 rounded-xl p-4 flex items-center justify-center h-16 animate-bounce border border-green-200">
                          <span className="text-green-700 font-bold text-sm">Gusto</span>
                        </div>
                        <div className="bg-blue-100 rounded-xl p-4 flex items-center justify-center h-16 animate-bounce border border-blue-200" style={{animationDelay: '0.1s'}}>
                          <span className="text-blue-700 font-bold text-sm">Square</span>
                        </div>
                        <div className="bg-blue-100 rounded-xl p-4 flex items-center justify-center h-16 animate-bounce border border-blue-200" style={{animationDelay: '0.2s'}}>
                          <span className="text-blue-700 font-bold text-sm">QuickBooks</span>
                        </div>
                        <div className="bg-orange-100 rounded-xl p-4 flex items-center justify-center h-16 animate-bounce border border-orange-200" style={{animationDelay: '0.3s'}}>
                          <span className="text-orange-700 font-bold text-sm">Toast</span>
                        </div>
                      </div>
                      
                      {/* Data Flow Animation */}
                      <div className="space-y-3">
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{width: '100%'}}></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{width: '75%', animationDelay: '0.5s'}}></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{width: '85%', animationDelay: '1s'}}></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Floating Elements */}
                    <div className="absolute top-4 right-4 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                    <div className="absolute bottom-4 left-4 w-3 h-3 bg-green-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-credentra-dark-teal mb-3">Data Sources</h3>
                  <p className="text-gray-600 leading-relaxed">Payroll Systems<br/>POS Data<br/>Employee Records</p>
                </div>
                
                {/* Step 2: Credentra AI Processing */}
                <div className="text-center group relative">
                  
                  {/* Connecting Arrows */}
                  <div className="hidden lg:block absolute -left-6 top-1/2 transform -translate-y-1/2 z-20">
                    <div className="flex items-center">
                      <div className="w-8 h-0.5 bg-credentra-bright-teal"></div>
                      <ArrowRight className="h-6 w-6 text-credentra-bright-teal animate-pulse ml-1" />
                    </div>
                  </div>
                  
                  <div className="hidden lg:block absolute -right-6 top-1/2 transform -translate-y-1/2 z-20">
                    <div className="flex items-center">
                      <ArrowRight className="h-6 w-6 text-credentra-bright-teal animate-pulse mr-1" style={{animationDelay: '0.5s'}} />
                      <div className="w-8 h-0.5 bg-credentra-bright-teal"></div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-3xl p-6 shadow-xl mb-6 transform group-hover:scale-105 transition-all duration-300 h-[340px] flex flex-col justify-center relative overflow-hidden" style={{background: '#112b3c'}}>
                    
                    {/* Background Effects */}
                    <div className="absolute inset-0">
                      <div className="absolute top-0 left-0 w-full h-full bg-white/5"></div>
                      <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
                      <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                    </div>
                    
                    <div className="relative z-10 space-y-4">
                      
                      {/* AI Icon - Smaller */}
                      <div className="flex justify-center">
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg border-2 border-white/30">
                          <Sparkles className="h-6 w-6 text-credentra-dark-teal animate-pulse" style={{animationDuration: '2s'}} />
                        </div>
                      </div>
                      
                      {/* Processing Steps - Compact */}
                      <div className="space-y-2">
                        <div className="bg-white rounded-lg p-3 shadow-md border border-white/20">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{background: '#112b3c'}}>
                                <Database className="h-3 w-3 text-white" />
                              </div>
                              <span className="text-credentra-dark-teal font-bold text-xs">Ingesting Data</span>
                            </div>
                            <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="w-full h-full bg-credentra-bright-teal rounded-full animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 shadow-md border border-white/20">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{background: '#112b3c'}}>
                                <Cpu className="h-3 w-3 text-white" />
                              </div>
                              <span className="text-credentra-dark-teal font-bold text-xs">AI Parsing</span>
                            </div>
                            <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="w-full h-full bg-credentra-bright-teal rounded-full animate-pulse" style={{animationDelay: '0.3s'}}></div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 shadow-md border border-white/20">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{background: '#112b3c'}}>
                                <Calculator className="h-3 w-3 text-white" />
                              </div>
                              <span className="text-credentra-dark-teal font-bold text-xs">FICA Calculating</span>
                            </div>
                            <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="w-full h-full bg-credentra-bright-teal rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 shadow-md border border-white/20">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{background: '#112b3c'}}>
                                <FileCheck className="h-3 w-3 text-white" />
                              </div>
                              <span className="text-credentra-dark-teal font-bold text-xs">Form Generation</span>
                            </div>
                            <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="w-full h-full bg-credentra-bright-teal rounded-full animate-pulse" style={{animationDelay: '0.9s'}}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-credentra-dark-teal mb-3">Credentra AI</h3>
                  <p className="text-gray-600 leading-relaxed">Automated Processing<br/>FICA Calculations<br/>Compliance Checks</p>
                </div>
                
                {/* Step 3: Ready Form 8846 */}
                <div className="text-center group">
                  <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-green-200 mb-6 transform group-hover:scale-105 transition-all duration-300 h-[340px] flex flex-col justify-center relative overflow-hidden">
                    
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-green-50 opacity-50"></div>
                    
                    <div className="relative z-10 space-y-6">
                      
                      {/* Form Icon */}
                      <div className="flex justify-center">
                        <div className="w-24 h-28 bg-green-100 rounded-2xl flex items-center justify-center border-2 border-green-300 animate-bounce relative">
                          <FileCheck className="h-12 w-12 text-green-600" />
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Form Details */}
                      <div className="space-y-4">
                        <div className="bg-green-100 border border-green-200 rounded-xl p-3">
                          <div className="text-green-700 font-bold text-sm">IRS FORM 8846</div>
                          <div className="text-green-600 text-xs mt-1">Credit for Employer Social Security and Medicare Taxes</div>
                        </div>
                        
                        {/* Success Indicators */}
                        <div className="flex justify-center space-x-2">
                          {[0, 0.2, 0.4].map((delay, i) => (
                            <div key={i} className="w-3 h-3 bg-green-500 rounded-full animate-pulse" style={{animationDelay: `${delay}s`}}></div>
                          ))}
                        </div>
                        
                        {/* Ready Status */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                          <div className="text-emerald-700 font-semibold text-xs">✓ Ready for Filing</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Success Elements */}
                    <div className="absolute top-4 right-4 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <div className="absolute bottom-6 left-6 w-3 h-3 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '1.5s'}}></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-credentra-dark-teal mb-3">Ready Form 8846</h3>
                  <p className="text-gray-600 leading-relaxed">IRS-Ready Filing<br/>CPA Review Ready<br/>Audit Trail Included</p>
                </div>
              </div>
              
            </div>
          </div>
        </div>
        
        {/* Background Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-20 w-1 h-1 bg-credentra-bright-teal/30 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-32 w-2 h-2 bg-credentra-bright-teal/20 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-credentra-bright-teal/30 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
      </section>

      {/* Problem Section - Redesigned with modern cards */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="section-badge text-red-600 bg-red-50">
            <TrendingUp className="h-3 w-3" />
            The Challenge
          </div>
          <h2 className="enterprise-heading text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
            Why This Matters
          </h2>
          <p className="enterprise-subheading text-xl leading-relaxed max-w-4xl mx-auto">
            Every restaurant fights tight margins. Our proprietary AI workflows turn tedious manual work into recovered cash flow - fast and accurate.
          </p>
          <div className="mt-8 text-center">
            <div className="inline-block rounded-xl px-8 py-4 shadow-lg" style={{background: 'linear-gradient(135deg, rgba(0, 214, 207, 0.1), rgba(0, 214, 207, 0.05))', border: '2px solid rgba(0, 214, 207, 0.2)'}}>
              <span className="text-2xl font-bold" style={{color: '#1f4761'}}>
                Credentra handles the heavy lift so you don't.
              </span>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* How It Works - Redesigned for better fit */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="section-badge text-white" style={{background: '#112b3c'}}>
              <Cpu className="h-3 w-3" />
              Process
            </div>
            <h2 className="enterprise-heading text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
              How It Works
            </h2>
            <p className="enterprise-subheading text-xl max-w-2xl mx-auto">
              Four simple steps to automate your FICA tip credit recovery
            </p>
          </div>
          
          {/* Redesigned Cards with Better Spacing */}
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
            {mockData.howItWorksSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 h-full border border-gray-100">
                  {/* Step Number */}
                  <div className="flex items-center justify-center w-12 h-12 rounded-full text-white font-bold text-lg mb-6" style={{background: '#112b3c'}}>
                    {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{background: 'rgba(0, 214, 207, 0.1)'}}>
                    <step.icon className="h-8 w-8" style={{color: '#00d6cf'}} />
                  </div>
                  
                  {/* Content */}
                  <div>
                    <h3 className="text-xl font-bold text-credentra-dark-teal mb-4 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
                
                {/* Connection Arrow (Desktop Only) */}
                {index < mockData.howItWorksSteps.length - 1 && (
                  <div className="hidden xl:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <ArrowRight className="h-5 w-5 text-credentra-bright-teal" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Button 
              size="lg"
              className="text-white font-semibold text-lg px-10 py-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
              onClick={handleEligibilityCheck}
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Start Calculation
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section - Premium redesign */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="section-badge text-credentra-bright-teal">
              <Database className="h-3 w-3" />
              Capabilities
            </div>
            <h2 className="enterprise-heading text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
              Enterprise-Grade Features
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium ml-4 align-middle">
                <Clock className="h-3 w-3" />
                Coming Soon
              </div>
            </h2>
            <p className="enterprise-subheading text-xl max-w-3xl mx-auto">
              Currently serving clients with bespoke Excel models via CPA partnerships while our automated platform completes development
            </p>
          </div>

          <Tabs defaultValue="connect" className="max-w-6xl mx-auto">
            <div className="flex justify-center mb-12">
              <TabsList className="modern-tabs bg-white p-2 shadow-lg">
                <TabsTrigger value="connect" className="modern-tab">
                  <Zap className="h-4 w-4 mr-2" />
                  Connect
                </TabsTrigger>
                <TabsTrigger value="verify" className="modern-tab">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Verify
                </TabsTrigger>
                <TabsTrigger value="security" className="modern-tab">
                  <Lock className="h-4 w-4 mr-2" />
                  Security
                </TabsTrigger>
                <TabsTrigger value="export" className="modern-tab">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="connect" className="features-grid">
              <div className="enterprise-card">
                <div className="enterprise-icon">
                  <Zap className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">One-click connections</h3>
                <p className="enterprise-subheading text-lg">Link POS/payroll in minutes; no messy spreadsheets.</p>
              </div>
              <div className="enterprise-card">
                <div className="enterprise-icon">
                  <Building className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">Multi-location support</h3>
                <p className="enterprise-subheading text-lg">Roll up or drill down across establishments.</p>
              </div>
            </TabsContent>
            
            <TabsContent value="verify" className="features-grid">
              <div className="enterprise-card">
                <div className="enterprise-icon">
                  <Brain className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">AI data checks</h3>
                <p className="enterprise-subheading text-lg">Automated validations to catch gaps and anomalies before filing.</p>
              </div>
              <div className="enterprise-card">
                <div className="enterprise-icon">
                  <Bell className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">Smart notifications</h3>
                <p className="enterprise-subheading text-lg">Monthly/quarterly nudges so you never miss credits.</p>
              </div>
            </TabsContent>
            
            <TabsContent value="security" className="features-grid">
              <div className="enterprise-card">
                <div className="enterprise-icon">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">Security-first</h3>
                <p className="enterprise-subheading text-lg">Encryption in transit and at rest; least-privilege access.</p>
              </div>
              <div className="enterprise-card">
                <div className="enterprise-icon">
                  <Eye className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">Role-based access</h3>
                <p className="enterprise-subheading text-lg">Owners, controllers, and CPAs each get the view they need.</p>
              </div>
            </TabsContent>
            
            <TabsContent value="export" className="features-grid">
              <div className="enterprise-card">
                <div className="enterprise-icon">
                  <Download className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">Evidence archive</h3>
                <p className="enterprise-subheading text-lg">Downloadable audit trail (source docs, calculations, rationale).</p>
              </div>
              <div className="enterprise-card">
                <div className="enterprise-icon">
                  <FileCheck className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">CPA-friendly export</h3>
                <p className="enterprise-subheading text-lg">Clean workpapers, summaries, and line-by-line detail.</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Social Proof - Enhanced */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="pricing-card">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
              <div className="text-white px-6 py-2 rounded-full font-bold text-sm flex items-center gap-2" style={{background: '#112b3c'}}>
                <Star className="h-4 w-4" />
                Limited Spots Available
              </div>
            </div>
            
            <div className="pt-8">
              <h3 className="text-3xl font-bold text-credentra-dark-teal mb-4">
                Start Risk-Free Today
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                No upfront costs, no monthly fees. You only pay when we successfully recover tax credits. 
                Schedule a meeting to get started.
              </p>
              <Button 
                size="lg"
                className="text-white font-semibold text-lg px-12 py-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
                onClick={handleScheduleCall}
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Schedule Meeting
              </Button>
            </div>
          </div>
        </div>
      </section>



      {/* Blog Section */}
      <BlogSection />

      {/* Pricing Section */}
      <PricingSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* Modern SaaS Footer - New Credentra Brand Gradient */}
      <footer className="text-white py-20" style={{background: '#112b3c'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="space-y-6 md:col-span-2">
              <img 
                src="/credentra-logo-new.png" 
                alt="Credentra Logo" 
                className="h-32 w-auto"
              />
              <p className="text-white/80 text-lg leading-relaxed max-w-md">
                AI-powered FICA tip credit recovery for restaurants and CPAs.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-white/60 text-sm">Questions?</span>
                <a 
                  href="mailto:jonathan@credentra.ai" 
                  className="text-credentra-bright-teal hover:text-white transition-colors text-sm font-medium"
                >
                  jonathan@credentra.ai
                </a>
              </div>
            </div>
            
            {/* Product */}
            <div className="space-y-6">
              <h3 className="text-credentra-bright-teal font-bold text-lg">Product</h3>
              <ul className="space-y-3">
                <li><a href="#features" className="text-white/70 hover:text-credentra-bright-teal transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-white/70 hover:text-credentra-bright-teal transition-colors">Pricing</a></li>
                <li><button onClick={handleWaitlistJoin} className="text-white/70 hover:text-credentra-bright-teal transition-colors text-left">Check Eligibility</button></li>
              </ul>
            </div>
            
            {/* Company */}
            <div className="space-y-6">
              <h3 className="text-credentra-bright-teal font-bold text-lg">Company</h3>
              <ul className="space-y-3">
                <li><a href="/about" className="text-white/70 hover:text-credentra-bright-teal transition-colors">About</a></li>
                <li><a href="#blog" className="text-white/70 hover:text-credentra-bright-teal transition-colors">Blog</a></li>
                <li><a href="mailto:jonathan@credentra.ai" className="text-white/70 hover:text-credentra-bright-teal transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="mt-16 pt-12 border-t border-white/20">
            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ready to recover your tax credits?
              </h3>
              <Button 
                size="lg"
                className="text-white font-semibold text-lg px-10 py-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
                onClick={handleEligibilityCheck}
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Start Calculation
              </Button>
            </div>
          </div>
          
          {/* Compliance Disclosures Section */}
          <div className="mt-16 pt-12 border-t border-white/20">
            <div className="bg-black/10 rounded-xl p-6 mb-8">
              <h4 className="text-white font-semibold mb-4 text-center">Important Disclosures</h4>
              <div className="text-white/70 text-sm leading-relaxed space-y-4">
                <p>
                  <strong>Current Service Model:</strong> Our automated software platform is currently in development. We are actively serving clients through partnerships with qualified accounting firms using robust, bespoke Excel models tailored to each business's specific payroll and tip structure. This ensures accurate FICA tip credit calculations while our AI-powered platform completes development.
                </p>
                <p>
                  <strong>Important Disclosure:</strong> Credentra provides software tools for organizing payroll and tip data but does not provide tax advice. All information is for educational purposes only. Tax credit recovery results may vary based on individual business circumstances, employee tip reporting, and other factors. Dollar amounts in examples are hypothetical and for illustrative purposes only. While Credentra assists with data organization and filing preparation, we strongly recommend that all tax credit claims be reviewed by a qualified tax professional or CPA before submission to the IRS. Users are responsible for ensuring compliance with all applicable IRS requirements and tax regulations. Credentra does not guarantee IRS acceptance of any tax credit claims or substitute for professional tax advice.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="mt-12 pt-8 border-t border-white/20">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-white/60 text-sm">
                © 2025 Credentra. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                {/* LinkedIn Social Media Icon */}
                <a 
                  href="https://www.linkedin.com/company/credentra/?viewAsMember=true" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors duration-200 hover:scale-110 transform"
                  title="Follow Credentra on LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a 
                  href="/llms.txt" 
                  target="_blank" 
                  className="text-white/60 hover:text-white/80 text-xs transition-colors duration-200"
                  title="AI Search Engine Information"
                >
                  llms.txt
                </a>
                <span className="text-white/40 text-xs">Made for restaurants and CPAs</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Credentra FICA AI Chat Widget */}
      <FormlessWidget />
    </div>
  );
};

export default LandingPage;